/**
 * FeatureGuard — generic nested route guard (defense-in-depth Gate A/UI Gate B).
 *
 * Implements show-not-tell/TS-004 (T2) and show-not-tell/US-004 (T3).
 * Direct structural clone of ParentGuard.tsx (itself modelled on AuthGuard.tsx):
 * renders the protected child routes (<Outlet/>) only when the given feature
 * flag is enabled in the dashboard features map; otherwise redirects to the
 * student dashboard.
 *
 * Registered NESTED INSIDE the existing AuthGuard route group in App.tsx
 * (<Route element={<AuthGuard/>}><Route element={<FeatureGuard feature="show_not_tell"/>}>...),
 * so FeatureGuard never re-checks auth — it composes on top of AuthGuard,
 * enforcing "FeatureGuard composes AuthGuard" (BR-TS004.1 / BR-008).
 *
 * This is UI-only defense in depth. The authoritative security boundary is
 * the backend Gate B (404 NOT_FOUND on every /sessions/show-not-tell/*
 * endpoint for non-entitled users) — hiding/redirecting here is UX only.
 *
 * Fail-safe direction: enforces TO-LL-036 (fail-safe/fail-closed external
 * reads) and TO-LL-003 (exact CCM-001 features contract shape) — an absent,
 * undefined, or errored features map is treated as feature-OFF and redirects
 * to the dashboard; the gated page is NEVER rendered on an ambiguous read.
 *
 * TO-LL-005: the redirect decision is gated behind BOTH auth isLoading AND
 * dashboard isLoading, so this guard never flashes a premature redirect
 * before entitlement is actually known.
 *
 * TO-LL-026: this guard only READS the dashboard query; it never invalidates
 * or forces a refetch — that is the dashboard surface's (TS-003) concern.
 *
 * "Turned off while on the page" (AC-TS004.2/AC-004.2): handled because
 * useDashboard re-fetches on its own staleTime/refetch cadence and this guard
 * re-evaluates reactively on every render, so a features flip to false
 * redirects on the next render without any extra wiring here.
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useDashboard } from '@/hooks/useDashboard';
import { ROUTES } from '@/constants/routes';
import type { DashboardFeatures } from '@/types/dashboard';

export interface FeatureGuardProps {
  /** The DashboardFeatures key to gate on, e.g. "show_not_tell". */
  feature: keyof DashboardFeatures;
}

/**
 * FeatureGuard — reusable entitlement gate for any future feature flag.
 * Register as: <Route element={<FeatureGuard feature="show_not_tell"/>}>...</Route>
 * nested INSIDE an existing <Route element={<AuthGuard/>}> group.
 */
export function FeatureGuard({ feature }: FeatureGuardProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { dashboard, isLoading: dashLoading } = useDashboard();
  const location = useLocation();

  // TO-LL-005: show the shared spinner while EITHER auth or the dashboard
  // query is still resolving — never decide the redirect before entitlement
  // is known.
  if (authLoading || dashLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
          <p className="mt-2 text-sm text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Belt-and-braces: AuthGuard (the outer nesting) already redirects
  // unauthenticated users to /login, but this guard never assumes it is the
  // only line of defense.
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Implements show-not-tell/TS-004 (T2); enforces TO-LL-036 (fail-safe to
  // feature-OFF on absent/undefined/errored features map — never fail-open
  // to the gated page) and TO-LL-003 (exact boolean-only CCM-001 shape).
  const enabled = dashboard?.features?.[feature] ?? false;
  if (!enabled) {
    return <Navigate to={ROUTES.STUDENT_HOME} replace />;
  }

  return <Outlet />;
}
